import { inject, Injectable } from '@angular/core';

import { map, mergeMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  BarApplicationConfigurationService,
  BrandApplicationConfigurationService,
  FrontDeskApplicationConfigurationService,
  HousekeepingApplicationConfigurationService,
  KioskApplicationConfigurationService,
  LocationApplicationConfigurationService,
  RoomApplicationConfigurationService,
  StaffApplicationConfigurationService,
} from '@era/settings-sdk';
import { EnvironmentService } from '@methylium/core';
import { Observable } from 'rxjs';
import { compareApplications, safeMap } from '../effects.helpers';
import { CategoryType } from '../shared/types';
import {
  ApplicationsApiActions,
  CategoryApplicationsEffectActions,
  CategoryApplicationsResolverActions,
  CategoryApplicationViewActions,
  DeviceConfiguredApplicationsResolverActions,
} from './category-applications.actions';
import { ApplicationConfiguration, applicationConfigurationFrom } from './category-applications.state';

@Injectable()
export class CategoryApplicationsEffects {
  protected features = inject(EnvironmentService).environment.features;

  constructor(
    private readonly actions: Actions,
    private readonly brandApplicationService: BrandApplicationConfigurationService,
    private readonly locationApplicationService: LocationApplicationConfigurationService,
    private readonly roomApplicationService: RoomApplicationConfigurationService,
    private readonly barApplicationService: BarApplicationConfigurationService,
    private readonly frontDeskApplicationService: FrontDeskApplicationConfigurationService,
    private readonly housekeepingApplicationService: HousekeepingApplicationConfigurationService,
    private readonly kioskApplicationService: KioskApplicationConfigurationService,
    private readonly staffApplicationService: StaffApplicationConfigurationService
  ) {}

  /**
   * The category application configuration has been updated.
   * Effect: we submit the update to the api and dispatch an error if applicable.
   */
  categoryApplicationLinkUpdatedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CategoryApplicationViewActions.applicationConfigurationsUpdated),
      mergeMap((action) =>
        safeMap(
          this.categoryApplicationLinkUpdatedEffectBuilder(action),
          () =>
            CategoryApplicationsEffectActions.categoryApplicationsRequested({
              id: action.id,
              categoryType: action.categoryType,
            }),
          (error) =>
            ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError({
              id: action.id,
              categoryType: action.categoryType,
              error,
            })
        )
      )
    );
  });

  /**
   * The (re)load of the category applications has been requested.
   * Effect: we load the state from the api.
   */
  categoryApplicationsRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CategoryApplicationsResolverActions.categoryApplicationsRequested,
        DeviceConfiguredApplicationsResolverActions.categoryApplicationsRequested,
        CategoryApplicationsEffectActions.categoryApplicationsRequested
      ),
      mergeMap((action) =>
        safeMap(
          this.categoryApplicationsRequestedEffectBuilder(action),
          (categoryApplications) =>
            ApplicationsApiActions.categoryApplicationsLoaded({
              applications: categoryApplications,
              categoryType: action.categoryType,
            }),
          (error) =>
            ApplicationsApiActions.categoryApplicationsLoadedError({
              error,
            })
        )
      )
    );
  });

  /**
   * The applications couldn't be updated.
   * Effect: we request a reload of the state from the api.
   */
  categoryApplicationsUpdatedErrorEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ApplicationsApiActions.categoryApplicationConfigurationsUpdatedError),
      map((action) =>
        CategoryApplicationsEffectActions.categoryApplicationsRequested({
          id: action.id,
          categoryType: action.categoryType,
        })
      )
    );
  });

  private categoryApplicationLinkUpdatedEffectBuilder(action: {
    id: string;
    categoryType: CategoryType;
    previousApplicationsLinkedToCategory: ApplicationConfiguration[];
    updatedApplicationsLinkedToCategory: ApplicationConfiguration[];
  }): Observable<unknown> {
    switch (action.categoryType) {
      case 'Brand':
        return this.brandApplicationService.updateAllApplicationsForBrand(action.id, compareApplications(action));
      case 'Location':
        return this.locationApplicationService.updateAllApplicationsForLocation(action.id, compareApplications(action));
      case 'Room':
        return this.roomApplicationService.updateAllApplicationsForRoom(action.id, compareApplications(action));
      case 'Bar':
        return this.barApplicationService.updateAllApplicationsForBar(action.id, compareApplications(action));
      case 'FrontDesk':
        return this.frontDeskApplicationService.updateAllApplicationsForFrontDesk(
          action.id,
          compareApplications(action)
        );
      case 'Housekeeping':
        return this.housekeepingApplicationService.updateAllApplicationsForHousekeeping(
          action.id,
          compareApplications(action)
        );
      case 'Kiosk':
        return this.kioskApplicationService.updateAllApplicationsForKiosk(action.id, compareApplications(action));
      case 'Staff':
        return this.staffApplicationService.updateAllApplicationsForStaff(action.id, compareApplications(action));
    }
  }

  private categoryApplicationsRequestedEffectBuilder(action: {
    id: string;
    categoryType: CategoryType;
  }): Observable<ApplicationConfiguration[]> {
    switch (action.categoryType) {
      case 'Brand':
        return this.brandApplicationService
          .getAllApplicationsForBrand(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Location':
        return this.locationApplicationService
          .getAllApplicationsForLocation(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Room':
        return this.roomApplicationService
          .getAllApplicationsForRoom(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Bar':
        return this.barApplicationService
          .getAllApplicationsForBar(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'FrontDesk':
        return this.frontDeskApplicationService
          .getAllApplicationsForFrontDesk(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Housekeeping':
        return this.housekeepingApplicationService
          .getAllApplicationsForHousekeeping(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Kiosk':
        return this.kioskApplicationService
          .getAllApplicationsForKiosk(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
      case 'Staff':
        return this.staffApplicationService
          .getAllApplicationsForStaff(action.id)
          .pipe(map((configurations) => configurations.map(applicationConfigurationFrom)));
    }
  }
}
